<script lang="ts">
	import ParticleNetwork from '$lib/components/ParticleNetwork.svelte';
	import { t, type Language } from '$lib/i18n';

	interface Props {
		lang: Language;
	}

	let { lang }: Props = $props();
	let tr = $derived(t(lang));
</script>

<section class="relative min-h-screen flex items-center overflow-hidden">
	<!-- Animated background (lighter version on mobile: particles + data streams only) -->
	<div class="absolute inset-0">
		<svelte:boundary>
			<ParticleNetwork />
			{#snippet failed()}
				<div class="absolute inset-0 bg-slate-950"></div>
			{/snippet}
		</svelte:boundary>
	</div>

	<!-- Lightweight mobile background orb (no blur filter, no animation) -->
	<div
		class="md:hidden absolute top-1/3 left-1/4 w-64 h-64 rounded-full bg-gradient-to-br from-[#0086B7]/20 to-indigo-500/10"
	></div>

	<!-- Glowing orbs (hidden on mobile for performance) -->
	<div
		class="hidden md:block absolute top-1/4 left-1/4 w-[700px] h-[700px] bg-[#0086B7]/30 rounded-full blur-[100px] animate-pulse"
		style="animation-duration: 8s;"
	></div>
	<div
		class="hidden md:block absolute bottom-1/3 right-1/4 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[80px] animate-pulse"
		style="animation-duration: 10s; animation-delay: 2s;"
	></div>
	<div
		class="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/15 rounded-full blur-[120px]"
	></div>

	<!-- Gradient overlays -->
	<div
		class="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/30 to-slate-950 hero-overlay"
	></div>

	<!-- Grid pattern -->
	<div class="absolute inset-0 bg-grid opacity-30"></div>

	<!-- Content -->
	<div class="relative max-w-7xl mx-auto px-6 py-12 pt-24 md:py-20 md:pt-32">
		<div class="max-w-3xl">
			<!-- Badge -->
			<div
				class="inline-flex items-center gap-3 px-4 py-2 rounded-full glass mb-8 animate-fade-in group hover:bg-white/[0.06] transition-all duration-500 cursor-default"
			>
				<span class="relative flex h-2 w-2">
					<span
						class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4ACB85] opacity-75"
					></span>
					<span class="relative inline-flex rounded-full h-2 w-2 bg-[#4ACB85]"></span>
				</span>
				<span class="text-sm text-slate-300 font-medium hero-badge-text">{tr('hero_badge')}</span>
			</div>

			<!-- Headline -->
			<h1
				class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 tracking-tight"
			>
				<span class="animate-slide-up block">{tr('hero_title_1')}</span>
				<span class="animate-slide-up stagger-1 block"
					><span class="text-gradient">{tr('hero_title_2')}</span></span
				>
			</h1>

			<!-- Subheadline -->
			<p
				class="text-lg md:text-xl text-slate-400 leading-relaxed mb-10 max-w-2xl animate-slide-up stagger-3 hero-subtitle"
			>
				{tr('hero_subtitle')}
			</p>

			<!-- CTAs -->
			<div class="flex flex-wrap gap-4 animate-slide-up stagger-4">
				<a href="#contact" class="btn-primary text-lg flex items-center gap-3 group">
					<span>{tr('hero_cta_primary')}</span>
					<svg
						aria-hidden="true"
						class="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M17 8l4 4m0 0l-4 4m4-4H3"
						/>
					</svg>
				</a>
				<a href="#analytics" class="btn-secondary text-lg flex items-center gap-3">
					<span>{tr('hero_cta_secondary')}</span>
					<span>👀</span>
				</a>
			</div>
		</div>
	</div>

	<!-- Scroll indicator -->
	<div
		class="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in stagger-5"
	>
		<span class="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-medium hero-scroll-text"
			>{tr('hero_scroll')}</span
		>
		<div
			class="w-6 h-9 rounded-full border border-slate-600/50 flex items-start justify-center p-1.5 backdrop-blur-sm hero-scroll-box"
		>
			<div class="w-1 h-2 bg-white/60 rounded-full animate-bounce hero-scroll-dot"></div>
		</div>
	</div>
</section>
